import React, { memo, useCallback, useMemo } from 'react'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import { EmptyInsurancePolicyProducts } from 'src/components/insurance-policy/grid/EmptyInsurancePolicyProducts'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  INSURANCE_POLICY_PRODUCTS_GRID_COLUMNS_STORAGE_KEY,
  insurancePolicyPageSizeOptions,
} from 'src/constants/insurance-policy/constants'
import { InsurancePolicyProductsTableFields } from 'src/constants/insurance-policy/enums'
import { buildInsurancePolicyProductColumns } from 'src/constants/insurance-policy/insurancePolicyProductGrid'
import type { InsurancePolicyProduct } from 'src/interfaces/insurancePolicy'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  /**
   * Taken as a prop, not from a selector: these rows are the selected policy's own nested
   * array, and the page already derives that policy out of the list by `permanentId`.
   */
  policyProducts: InsurancePolicyProduct[]
  canManage: boolean
  onEditPolicyProduct(policyProduct: InsurancePolicyProduct): void
  onDeletePolicyProduct(policyProduct: InsurancePolicyProduct): void
}

const getRowId = (row: InsurancePolicyProduct) => {
  return row.id
}

const UNHIDEABLE_COLUMNS: Array<string> = [
  InsurancePolicyProductsTableFields.product,
  InsurancePolicyProductsTableFields.actions,
]

const InsurancePolicyProductsTableComponent = ({
  policyProducts,
  canManage,
  onEditPolicyProduct,
  onDeletePolicyProduct,
}: Props) => {
  const { paginationModel, setPaginationModel } = useGridPagination(insurancePolicyPageSizeOptions)

  const policyProductColumns = useMemo(() => {
    return buildInsurancePolicyProductColumns(onEditPolicyProduct, onDeletePolicyProduct, canManage)
  }, [onEditPolicyProduct, onDeletePolicyProduct, canManage])

  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    policyProductColumns,
    INSURANCE_POLICY_PRODUCTS_GRID_COLUMNS_STORAGE_KEY,
  )

  const columnVisibilityModel = useMemo(() => {
    return policyProductColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [policyProductColumns, includedColumns])

  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      policyProductColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [policyProductColumns, handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  return (
    <GridTable<InsurancePolicyProduct>
      rows={policyProducts}
      columns={policyProductColumns}
      getRowId={getRowId}
      // The rows come from the already-loaded policy, so there is no second request to wait on.
      isLoading={false}
      // Nothing sits above this grid — it is the whole card, so all four corners round.
      isStandalone
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={insurancePolicyPageSizeOptions}
      initialState={{
        sorting: {
          sortModel: [{ field: InsurancePolicyProductsTableFields.product, sort: 'asc' }],
        },
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyInsurancePolicyProducts}
      tableDataTestId="insurance-policy-products-list-table"
      rowDataTestIdPrefix="insurance-policy-products-list"
    />
  )
}

export const InsurancePolicyProductsTable = memo(InsurancePolicyProductsTableComponent)
